import React, { useState, useEffect } from 'react';
import { useConfirm } from '../context/ConfirmContext';
import { useNavigate } from 'react-router-dom';
import { Activity } from '../types';
import { activitiesAPI } from '../services/api';
import Pagination from './Pagination';
import { usePagination } from '../hooks/usePagination';
import { formatDate } from '../utils/dateFormat';

const Activities: React.FC = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const confirm = useConfirm();

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    try {
      setLoading(true);
      const data = await activitiesAPI.getActivities();
      setActivities(data);
      setError(null);
    } catch (err) {
      console.error('Error loading activities:', err);
      setError('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    const isConfirmed = await confirm({
      title: 'Delete activity',
      message: 'Are you sure you want to delete this activity? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger'
    });
    if (!isConfirmed) return;
    try {
      await activitiesAPI.deleteActivity(id);
      await loadActivities();
    } catch (err) {
      console.error('Error deleting activity:', err);
      setError('Failed to delete activity');
    }
  };

  const handleEdit = (activity: Activity) => {
    navigate(`/activities/${activity.referenceType}/${activity.referenceId}/edit/${activity.id}`);
  };

  const handleViewReferenceDetails = (activity: Activity) => {
    if (activity.referenceType.toLowerCase() === 'customer') {
      navigate(`/customers/${activity.referenceId}`);
    } else if (activity.referenceType.toLowerCase() === 'project') {
      navigate(`/projects/${activity.referenceId}`);
    }
  };

  const filteredActivities = activities.filter(activity =>
    activity.title.toLowerCase().includes(searchText.toLowerCase()) ||
    activity.referenceType.toLowerCase().includes(searchText.toLowerCase())
  );

  const {
    currentPage,
    pageSize,
    paginatedData: paginatedActivities,
    totalItems,
    goToPage,
    setPageSize
  } = usePagination({ data: filteredActivities, initialPageSize: 10 });

  return (
    <div className="fade-in">
      <div style={{ marginBottom: '2rem' }}>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1
              style={{
                color: 'var(--text-primary)',
                fontSize: 'var(--font-size-3xl)',
                fontWeight: '700',
                marginBottom: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
              }}
            >
              Activities
            </h1>
            <p
              style={{
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-lg)',
                margin: 0,
              }}
            >
              View and manage system activities and logs
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          <div className="spinner-border" role="status"></div>
          <p style={{ color: 'var(--text-muted)' }}>Loading activities...</p>
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <h5 className="card-title mb-0">Activity Log</h5>
              <div className="ms-auto d-flex flex-wrap gap-2 align-items-end" style={{ justifyContent: 'flex-end' }}>
                <div>
                  <input
                    type="text"
                    id="searchText"
                    className="form-control"
                    placeholder="Search by title or reference type..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th className="text-start">Title</th>
                    <th className="text-start">Created Date</th>
                    <th className="text-start">Reference Type</th>
                    <th className="text-start">Reference ID</th>
                    <th className="text-start">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedActivities.map((activity) => (
                    <tr key={activity.id}>
                      <td>{activity.title}</td>
                      <td>{formatDate(activity.createdDate)}</td>
                      <td>{activity.referenceType}</td>
                      <td>
                        {activity.referenceType.toLowerCase() === 'customer' ||
                        activity.referenceType.toLowerCase() === 'project' ? (
                          <button
                            className="btn btn-link p-0 text-decoration-underline"
                            style={{
                              color: 'var(--primary)',
                              textDecorationColor: 'var(--primary)',
                              textDecorationLine: 'underline',
                              textUnderlineOffset: '2px',
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                            onClick={() => handleViewReferenceDetails(activity)}
                          >
                            {activity.referenceId}
                          </button>
                        ) : (
                          activity.referenceId
                        )}
                      </td>
                      <td>
                        <div className="btn-group action-group" role="group">
                          <button
                            className="btn btn-sm action-btn action-edit"
                            onClick={() => handleEdit(activity)}
                            title="Edit"
                          >
                            <i className="fa-solid fa-pen" aria-hidden="true" />
                          </button>

                          <button
                            className="btn btn-sm action-btn action-delete"
                            onClick={() => handleDelete(activity.id)}
                            title="Delete"
                          >
                            <i className="fa-solid fa-trash" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalItems === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '2rem 1rem 0',
                  color: 'var(--text-muted)',
                }}
              >
            <h5>No activities found</h5>
                <p>Try adjusting your search or filters.</p>
              </div>
            ) : (
              <Pagination
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={pageSize}
                onPageChange={goToPage}
                onPageSizeChange={setPageSize}
                pageSizeOptions={[10, 25, 50, 100]}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Activities;
